import React, { useState, useMemo, useEffect } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  LayoutGrid,
  Table2
} from 'lucide-react'
import * as XLSX from 'xlsx'
import ActionMenu from './ActionMenu'
import './DataTable.css'

const DataTable = ({
  data = [],
  columns = [],
  selectedRows = [],
  setSelectedRows = [],
  enableSelection = false,
  enablePagination = true,
  pageSize: initialPageSize = 10,
  className = '',
  title = 'Data Table',
  onRowClick = null,
  onEdit = null,
  onDelete = null,
  onView = null,
  onApprove = null,
  onCancel = null,
  onRevisionHistory = null,
  customActions = null,
  loading = false,
  noCard = false,
  /** Force view: 'auto' | 'table' | 'cards' */
  viewMode: viewModeProp = 'auto'
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  )
  const [manualView, setManualView] = useState(null) // 'table' | 'cards' | null

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mq = window.matchMedia('(max-width: 768px)')
    const onChange = (e) => setIsNarrow(e.matches)
    setIsNarrow(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, pageSize, data])

  const resolvedView = useMemo(() => {
    if (viewModeProp === 'table' || viewModeProp === 'cards') return viewModeProp
    if (manualView) return manualView
    return isNarrow ? 'cards' : 'table'
  }, [viewModeProp, manualView, isNarrow])

  const displayColumns = useMemo(
    () => columns.filter((col) => {
      const key = col.accessorKey || col.key || col.id
      return key !== 'actions'
    }),
    [columns]
  )

  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return []
    let filtered = data
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      filtered = filtered.filter((row) => {
        if (!row) return false
        return columns.some((column) => {
          const value = row[column.accessorKey || column.key]
          if (value === null || value === undefined) return false
          if (typeof value === 'object') {
            return JSON.stringify(value).toLowerCase().includes(q)
          }
          return value.toString().toLowerCase().includes(q)
        })
      })
    }
    return filtered
  }, [data, searchTerm, columns])

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }
      return 0
    })
  }, [filteredData, sortConfig])

  const paginatedData = useMemo(() => {
    if (!enablePagination) return sortedData
    const startIndex = (currentPage - 1) * pageSize
    return sortedData.slice(startIndex, startIndex + pageSize)
  }, [sortedData, currentPage, pageSize, enablePagination])

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize) || 1)

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)
  }

  const handleRowSelect = (rowId, isSelected) => {
    if (isSelected) setSelectedRows((prev) => [...prev, rowId])
    else setSelectedRows((prev) => prev.filter((id) => id !== rowId))
  }

  const handleSelectAll = (isSelected) => {
    if (isSelected) setSelectedRows(paginatedData.map((row) => row.id))
    else setSelectedRows([])
  }

  const handleExport = () => {
    const exportData = selectedRows.length > 0
      ? data.filter((row) => selectedRows.includes(row.id))
      : sortedData
    const worksheet = XLSX.utils.json_to_sheet(
      exportData.map((row) => {
        const exportRow = {}
        columns.forEach((column) => {
          const key = column.accessorKey || column.key
          const label = column.header || column.label
          const val = row[key]
          exportRow[label] = typeof val === 'object' && val !== null ? JSON.stringify(val) : val
        })
        return exportRow
      })
    )
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')
    XLSX.writeFile(workbook, `export_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc'
        ? <ArrowUp size={16} className="text-primary" />
        : <ArrowDown size={16} className="text-primary" />
    }
    return <ArrowUpDown size={16} className="text-muted" />
  }

  const renderCell = (row, column) => {
    if (!row || !column) return null
    const value = row[column.accessorKey || column.key]
    const renderFunction = column.cell || column.render

    if (renderFunction) {
      try {
        if (column.cell) {
          return column.cell({ getValue: () => value, row: { original: row } })
        }
        return column.render(value, row)
      } catch (error) {
        console.error('Error in column render function:', error)
        return String(value || '')
      }
    }

    if (typeof value === 'object' && value !== null) {
      if (value.customerName) return value.customerName
      if (value.projectTypeName) return value.projectTypeName
      if (value.employeeName) return value.employeeName
      if (value.name) return value.name
      return JSON.stringify(value)
    }

    return String(value ?? '')
  }

  const plainCellText = (row, column) => {
    const value = row[column.accessorKey || column.key]
    if (value === null || value === undefined) return '—'
    if (typeof value === 'object') {
      return value.customerName || value.name || value.assetTypeName || value.label || '—'
    }
    return String(value)
  }

  const buildActionsForRow = (row) => {
    if (customActions) {
      return typeof customActions === 'function'
        ? customActions(row).map((action) => ({
            ...action,
            onClick: action.onClick ? () => action.onClick() : undefined
          }))
        : customActions.map((action) => ({
            ...action,
            onClick: action.onClick ? () => action.onClick(row) : undefined
          }))
    }
    return [
      ...(onView ? [{ type: 'view', label: 'View Details' }] : []),
      ...(onEdit ? [{ type: 'edit', label: 'Edit' }] : []),
      ...(onApprove ? [{ type: 'approve', label: 'Approve', icon: <CheckCircle size={16} /> }] : []),
      ...(onCancel ? [{ type: 'cancel', label: 'Cancel', icon: <XCircle size={16} /> }] : []),
      ...(onRevisionHistory ? [{ type: 'history', label: 'Revision History', onClick: () => onRevisionHistory(row) }] : []),
      ...(onDelete ? [{ type: 'delete', label: 'Delete' }] : [])
    ]
  }

  const handleAction = (action, row) => {
    if (action.onClick) {
      action.onClick()
      return
    }
    switch (action.type) {
      case 'view':
        onView && onView(row)
        break
      case 'edit':
        onEdit && onEdit(row)
        break
      case 'approve':
        onApprove && onApprove(row)
        break
      case 'cancel':
        onCancel && onCancel(row)
        break
      case 'delete':
        onDelete && onDelete(row)
        break
      default:
        break
    }
  }

  const hasInlineActions = columns.some(
    (col) => col.accessorKey === 'actions' || col.key === 'actions' || col.id === 'actions'
  )

  const renderActionMenu = (row) => {
    if (hasInlineActions) return null
    const actions = buildActionsForRow(row)
    if (!actions.length) return null
    return (
      <ActionMenu
        actions={actions}
        onAction={(action) => handleAction(action, row)}
        size="sm"
        variant="outline"
      />
    )
  }

  const primaryColumn = displayColumns[0]
  const secondaryColumns = displayColumns.slice(1, 6)

  const toolbar = (
    <div className={noCard ? 'datatable-search-section-no-card' : 'card-body datatable-search-section'}>
      <div className="datatable-toolbar">
        <div className="datatable-search-container">
          <input
            type="search"
            className="form-control datatable-search-input"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search table"
          />
        </div>
        <div className="datatable-toolbar-actions">
          <div className="datatable-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`datatable-view-btn ${resolvedView === 'table' ? 'active' : ''}`}
              onClick={() => setManualView('table')}
              title="Table view"
              aria-pressed={resolvedView === 'table'}
            >
              <Table2 size={16} />
            </button>
            <button
              type="button"
              className={`datatable-view-btn ${resolvedView === 'cards' ? 'active' : ''}`}
              onClick={() => setManualView('cards')}
              title="Card view"
              aria-pressed={resolvedView === 'cards'}
            >
              <LayoutGrid size={16} />
            </button>
          </div>
          <select
            className="datatable-page-size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm datatable-btn datatable-export-btn"
            onClick={handleExport}
            disabled={data.length === 0}
            title="Export Excel"
          >
            <FileSpreadsheet size={18} />
          </button>
          <span className="datatable-info-text">
            {paginatedData.length} of {sortedData.length}
            {selectedRows.length > 0 && ` · ${selectedRows.length} selected`}
          </span>
        </div>
      </div>
    </div>
  )

  const paginationFooter = enablePagination && sortedData.length > 0 && (
    <div className={noCard ? 'datatable-footer-no-card' : 'card-footer datatable-footer'}>
      <div className="datatable-pagination-bar">
        <div className="datatable-pagination-info">
          Page {currentPage} of {totalPages} ({sortedData.length} total)
        </div>
        <nav aria-label="Table pagination">
          <ul className="pagination pagination-sm mb-0 datatable-pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link datatable-page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
              </button>
            </li>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum
              if (totalPages <= 5) pageNum = i + 1
              else if (currentPage <= 3) pageNum = i + 1
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i
              else pageNum = currentPage - 2 + i
              return (
                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                  <button
                    type="button"
                    className="page-link datatable-page-btn"
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              )
            })}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link datatable-page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={16} />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  )

  const cardsView = (
    <div className="datatable-cards" role="list">
      {loading && (
        <div className="datatable-cards-empty">
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
          <span>Loading data...</span>
        </div>
      )}
      {!loading && paginatedData.length === 0 && (
        <div className="datatable-cards-empty">No data available</div>
      )}
      {!loading && paginatedData.map((row, index) => (
        <article
          key={row.id || index}
          className={`datatable-card-item ${selectedRows.includes(row.id) ? 'selected' : ''} ${onRowClick ? 'clickable' : ''}`}
          role="listitem"
          onClick={() => onRowClick && onRowClick(row)}
        >
          <div className="datatable-card-item-header">
            <div className="datatable-card-item-title-wrap">
              {enableSelection && (
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedRows.includes(row.id)}
                  onChange={(e) => {
                    e.stopPropagation()
                    handleRowSelect(row.id, e.target.checked)
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <div>
                <div className="datatable-card-item-title">
                  {primaryColumn ? renderCell(row, primaryColumn) : `Row ${index + 1}`}
                </div>
                {primaryColumn && (
                  <div className="datatable-card-item-subtitle">
                    {primaryColumn.header || primaryColumn.label}
                  </div>
                )}
              </div>
            </div>
            <div className="datatable-card-item-actions" onClick={(e) => e.stopPropagation()}>
              {renderActionMenu(row)}
            </div>
          </div>
          <div className="datatable-card-item-body">
            {secondaryColumns.map((column) => (
              <div key={column.accessorKey || column.key || column.id} className="datatable-card-field">
                <span className="datatable-card-field-label">{column.header || column.label}</span>
                <span className="datatable-card-field-value">{renderCell(row, column)}</span>
              </div>
            ))}
          </div>
        </article>
      ))}
    </div>
  )

  const tableView = (
    <div className="table-responsive datatable-table-wrap">
      <table className="table table-hover mb-0 datatable-table">
        <thead className="table-light datatable-thead">
          <tr>
            {enableSelection && (
              <th className="datatable-checkbox-column">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedRows.length === paginatedData.length && paginatedData.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            )}
            {columns.map((column) => {
              const align = column.align || column.meta?.align || 'left'
              const rightAligned = align === 'right'
              return (
              <th
                key={column.accessorKey || column.key || column.id}
                className="datatable-th sortable"
                style={{
                  width: column.width || 'auto',
                  textAlign: align
                }}
                onClick={() => handleSort(column.accessorKey || column.key)}
              >
                <div
                  className="d-flex align-items-center gap-2"
                  style={{
                    width: '100%',
                    flexDirection: rightAligned ? 'row-reverse' : 'row',
                    justifyContent: align === 'center' ? 'center' : 'flex-start'
                  }}
                >
                  {column.header || column.label}
                  {getSortIcon(column.accessorKey || column.key)}
                </div>
              </th>
              )
            })}
            {!hasInlineActions && (
              <th className="datatable-actions-column">Action</th>
            )}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={columns.length + (enableSelection ? 1 : 0) + (hasInlineActions ? 0 : 1)}
                className="text-center py-4 text-muted datatable-empty"
              >
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  Loading data...
                </div>
              </td>
            </tr>
          ) : paginatedData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (enableSelection ? 1 : 0) + (hasInlineActions ? 0 : 1)}
                className="text-center py-4 text-muted datatable-empty"
              >
                No data available
              </td>
            </tr>
          ) : (
            paginatedData.map((row, index) => (
              <tr
                key={row.id || index}
                className={`datatable-row ${selectedRows.includes(row.id) ? 'table-active' : ''} ${onRowClick ? 'clickable' : ''}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {enableSelection && (
                  <td className="datatable-checkbox-cell">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedRows.includes(row.id)}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleRowSelect(row.id, e.target.checked)
                      }}
                    />
                  </td>
                )}
                {columns.map((column) => {
                  const align = column.align || column.meta?.align || 'left'
                  return (
                  <td
                    key={column.accessorKey || column.key || column.id}
                    className="datatable-cell"
                    style={{ textAlign: align }}
                  >
                    {renderCell(row, column)}
                  </td>
                  )
                })}
                {!hasInlineActions && (
                  <td className="datatable-actions-cell">
                    <div className="d-flex justify-content-center">
                      {renderActionMenu(row)}
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )

  const tableContent = (
    <>
      {!noCard && (
        <div className="card-header datatable-header">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="mb-0 datatable-title">{title}</h5>
          </div>
        </div>
      )}
      {toolbar}
      {resolvedView === 'cards' ? cardsView : tableView}
      {paginationFooter}
    </>
  )

  // silence unused helper warning in some builds
  void plainCellText

  if (noCard) {
    return (
      <div className={`datatable-wrapper-no-card datatable-root view-${resolvedView} ${className}`}>
        {tableContent}
      </div>
    )
  }

  return (
    <div className={`card datatable-card datatable-root view-${resolvedView} ${className}`}>
      {tableContent}
    </div>
  )
}

export default DataTable
